import "@kdose/combobox-component";

export class AddressAutocompletion {
	/**
	 * Adresswidget mit Autovervollständigung und Statusanzeige.
	 *
	 * @param {HTMLInputElement} zipInput
	 * @param {HTMLInputElement} streetInput
	 * @param {HTMLInputElement} cityInput
	 * @param {HTMLInputElement|null} districtInput
	 * @param {HTMLInputElement} houseNumberInput
	 * @param {HTMLInputElement|null} addressAdditionInput
	 * @param {HTMLElement} ariaLiveElement HTML-Element für Screenreader-Vorlesung (muss aria-live enthalten).
	 * @param {HTMLElement} statusElement HTML-Element für die Statusanzeige der Adressüberprüfung.
	 * @param {string} csrf CSRF-Token für die XHR-Aufrufe.
	 * @param {function(boolean):void} validationCallback Callback für die Adress-Validierung.
	 * @param {HTMLInputElement|null} skipValidationInput Checkbox zum Überspringen der Adress-Validierung
	 */
	constructor(
		zipInput,
		streetInput,
		cityInput,
		districtInput,
		houseNumberInput,
		addressAdditionInput,
		ariaLiveElement,
		statusElement,
		csrf,
		validationCallback = null,
		skipValidationInput = null,
	) {
		this.zipInput = zipInput;
		this.streetInput = streetInput;
		this.cityInput = cityInput;
		this.districtInput = districtInput;
		this.houseNumberInput = houseNumberInput;
		this.addressAdditionInput = addressAdditionInput;
		this.ariaLiveElement = ariaLiveElement;
		this.statusElement = statusElement;
		this.csrf = csrf;
		this.validationCallback = validationCallback;
		this.skipValidationInput = skipValidationInput;

		this.addressAutocompletion();
	}

	addressAutocompletion() {
		if (this.skipValidationInput !== null) {
			this.skipValidationInput.addEventListener("change", () =>
				this.skipValidationInputChange(),
			);

			if (this.skipValidationInput.checked) {
				return;
			}
		}

		this.zipInput = this.makeSelect(this.zipInput, true);
		this.streetInput = this.makeSelect(this.streetInput);

		this.validateZip();
		this.validateAddress();

		this.addEventListener();
	}

	addEventListener() {
		const inputs = [
			this.zipInput,
			this.streetInput,
			this.cityInput,
			this.districtInput,
			this.houseNumberInput,
		].filter((el) => !!el);

		for (const el of inputs) {
			el.addEventListener("change", () => this.validateAddress());
		}

		this.zipInput.addEventListener("change", this.validateZip.bind(this));
		this.streetInput.addEventListener("change", this.validateStreet.bind(this));

		const zipCombobox = this.zipInput.closest("kdo-combobox");
		const streetCombobox = this.streetInput.closest("kdo-combobox");

		if (!zipCombobox || !streetCombobox) {
			return;
		}

		zipCombobox.options = async (query) => {
			const result = await this.fetchLocations(query);
			return result.map((item) => ({
				value: item.value,
				displayValue: `${item.zip} (${item.city})`,
				customProperties: {
					city: item.city,
				},
			}));
		};

		zipCombobox.addEventListener("selectedOption", async (evt) => {
			const initialSelection =
				this.zipInput.querySelector(".initial-selection");
			if (initialSelection) {
				initialSelection.remove();
			}
			this.cityInput.value = evt.detail.customProperties.city || "";
			streetCombobox.reset();
			if (this.districtInput) {
				this.districtInput.value = "";
			}
			this.validateZip();
			this.validateAddress();
		});

		streetCombobox.options = async (query) => {
			const result = await this.fetchStreets(query);
			return result.map((item) => ({
				value: item.name,
				displayValue: item.district
					? `${item.name} (${item.district})`
					: item.name,
				customProperties: {
					district: item.district,
				},
			}));
		};

		streetCombobox.addEventListener("selectedOption", (evt) => {
			const initialSelection =
				this.streetInput.querySelector(".initial-selection");
			if (initialSelection) {
				initialSelection.remove();
			}
			if (this.districtInput && evt.detail.customProperties.district) {
				this.districtInput.value = evt.detail.customProperties.district;
			}
			this.validateStreet();
			this.validateAddress();
		});
	}

	skipValidationInputChange() {
		if (
			(this.zipInput.nodeName === "SELECT" &&
				this.skipValidationInput.checked) ||
			(this.zipInput.nodeName === "INPUT" && !this.skipValidationInput.checked)
		) {
			if (this.skipValidationInput.checked) {
				this.zipInput = this.makeInput(this.zipInput);
				this.streetInput = this.makeInput(this.streetInput);
				this.addEventListener();
			} else {
				this.addressAutocompletion();
			}
		}

		this.validateZip();
		this.validateStreet();
		this.validateAddress();
	}

	/**
	 * @param {HTMLInputElement} input
	 * @returns {HTMLSelectElement}
	 */
	makeSelect(input, duplicateItemsAllowed = false) {
		const value = input.value;
		const name = input.name;
		const id = input.id;
		const className = input.className;
		const describedBy = input.getAttribute("aria-describedby");

		const combobox = document.createElement("kdo-combobox");
		combobox.id = `${id}_combobox`;
		combobox.setAttribute("debounce", "400");
		combobox.setAttribute("duplicate-items-allowed", duplicateItemsAllowed);
		const select = document.createElement("select");
		select.name = name;
		select.id = id;
		select.className = className;
		select.setAttribute("aria-describedby", describedBy);
		const emptyOption = document.createElement("option");
		emptyOption.value = "";
		emptyOption.innerText = "Bitte auswählen";
		select.appendChild(emptyOption);
		if (value) {
			const selectedOption = document.createElement("option");
			selectedOption.innerText = value;
			selectedOption.selected = true;
			selectedOption.classList.add("initial-selection");
			select.appendChild(selectedOption);
		}
		combobox.appendChild(select);

		input.replaceWith(combobox);
		return select;
	}

	/**
	 * @param {HTMLSelectElement} select
	 * @returns {HTMLInputElement}
	 */
	makeInput(select) {
		const selectedOption = select.querySelector("option[selected]") || {};
		const value = selectedOption.value || "";
		const name = select.name;
		const id = select.id;
		const className = select.className.replace("choices__input", "");
		const describedBy = select.getAttribute("aria-describedby");

		const input = document.createElement("input");
		input.name = name;
		input.id = id;
		input.setAttribute("aria-describedby", describedBy);
		input.value = value;
		input.className = className;

		select.closest("kdo-combobox").replaceWith(input);
		return input;
	}

	async validateZip() {
		const inputValue = this.zipInput.value;
		this.cityInput.readOnly =
			this.skipValidationInput === null || !this.skipValidationInput.checked;

		if (inputValue.trim() === "") {
			this.cityInput.value = "";
			this.streetInput.value = "";
			this.houseNumberInput.value = "";
			this.hideRow(this.cityInput);
			this.hideRow(this.streetInput);
			this.hideRow(this.houseNumberInput);
			return;
		}

		this.hideRow(this.cityInput, false);
		this.hideRow(this.streetInput, false);
	}

	async validateStreet() {
		const inputValue = this.streetInput.value;
		if (inputValue.trim() === "") {
			this.houseNumberInput.value = "";
		}
		this.hideRow(this.houseNumberInput, inputValue.trim() === "");
	}

	async validateAddress() {
		this.statusElement.classList.add("alert");
		this.statusElement.classList.remove("alert-danger");
		this.statusElement.classList.remove("alert-warning");
		this.statusElement.classList.remove("alert-success");
		this.statusElement.classList.remove("alert-info");

		if (
			this.zipInput.value === "" ||
			this.streetInput.value === "" ||
			this.cityInput.value === "" ||
			this.houseNumberInput.value === ""
		) {
			this.statusElement.textContent =
				"Bitte geben Sie alle Pflichtfelder der Adresse ein.";
			this.statusElement.classList.add("alert-warning");
			this.hideRow(this.districtInput);
			this.hideRow(this.addressAdditionInput);
			return;
		}

		this.hideRow(this.districtInput, false);
		this.hideRow(this.addressAdditionInput, false);

		this.statusElement.textContent = "Ihre Adresse wird zur Zeit überprüft.";
		this.statusElement.classList.add("alert-info");

		const formData = new FormData();
		formData.append("zip", this.zipInput.value);
		formData.append("street", this.streetInput.value);
		formData.append("houseNumber", this.houseNumberInput.value);
		formData.append("city", this.cityInput.value);
		if (this.districtInput) {
			formData.append("district", this.districtInput.value);
		}
		formData.append("token", this.csrf);

		const source = await fetch("/valid_address", {
			headers: {
				Accept: "application/json",
				"X-Requested-With": "XMLHttpRequest",
			},
			method: "POST",
			body: formData,
		});
		const response = await source.json();

		if (response.valid) {
			this.statusElement.textContent = "Diese Adresse ist gültig.";
			this.statusElement.classList.remove("alert-info");
			this.statusElement.classList.add("alert-success");

			if (typeof this.validationCallback === "function") {
				this.validationCallback(true);
			}
			return;
		}

		this.statusElement.textContent = `Diese Adresse ist ungültig.
      Bitte prüfen Sie die offiziellen Schreibweisen. Falls Sie der Meinung sind, dass Sie Ihre Adresse richtig eingegeben haben,
      wenden Sie sich bitte telefonisch oder per E-Mail an uns, sodass wir eine Anmeldung und Zuordnung manuell vornehmen können.
    `;
		this.statusElement.classList.remove("alert-info");
		this.statusElement.classList.add("alert-danger");

		if (typeof this.validationCallback === "function") {
			this.validationCallback(false);
		}
	}

	/**
	 * @param {HTMLElement|null} input
	 * @param {boolean} hidden
	 */
	hideRow(input, hidden = true) {
		let isHidden = hidden;

		if (input === null) {
			return;
		}

		if (this.skipValidationInput?.checked) {
			isHidden = false;
		}

		input.closest(".form-group").classList.toggle("hidden", isHidden);
	}

	/**
	 * @param {HTMLElement} input
	 * @returns {HTMLElement}
	 */
	getErrorElement(input) {
		let errorElement = input.parentElement.querySelector(".custom-error");
		if (errorElement !== null) {
			return errorElement;
		}
		errorElement = document.createElement("div");
		errorElement.classList.add("custom-error", "text-danger");
		input.insertAdjacentElement("afterend", errorElement);
		return errorElement;
	}

	/**
	 * @param {string} zip
	 * @returns {object}
	 */
	async fetchLocations(zip) {
		try {
			const formData = new FormData();
			formData.append("zip", zip);
			formData.append("token", this.csrf);
			const response = await fetch("/get_locations_by_zip", {
				headers: {
					Accept: "application/json",
					"X-Requested-With": "XMLHttpRequest",
				},
				method: "POST",
				body: formData,
			});
			return await response.json();
		} catch (error) {
			return error;
		}
	}

	/**
	 * @param {string} street
	 * @returns {object}
	 */
	async fetchStreets(street) {
		try {
			const formData = new FormData();
			formData.append("zip", this.zipInput.value);
			formData.append("city", this.cityInput.value);
			formData.append("street", street);
			formData.append("token", this.csrf);
			const response = await fetch("/get_streets", {
				headers: {
					Accept: "application/json",
					"X-Requested-With": "XMLHttpRequest",
				},
				method: "POST",
				body: formData,
			});
			return await response.json();
		} catch (error) {
			return error;
		}
	}
}
